import { useState } from "react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormSectionType, SectionType } from "@/models";
import { Label } from "@/components/ui/label";

interface AddSectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddSection: (data: { 
    section_title: string, 
    section_subtitle: string,
    section_type: FormSectionType, 
    multiple_elements: boolean 
  }) => Promise<void>;
  isSubmitting: boolean;
}

const AddSectionDialog = ({
  isOpen,
  onClose,
  onAddSection,
  isSubmitting
}: AddSectionDialogProps) => {
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionSubtitle, setSectionSubtitle] = useState("");
  const [sectionType, setSectionType] = useState<FormSectionType>("Default");
  const [multipleElements, setMultipleElements] = useState(false);
  
  const handleAddSection = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    await onAddSection({
      section_title: sectionTitle,
      section_subtitle: sectionSubtitle,
      section_type: sectionType,
      multiple_elements: multipleElements
    });
  };
  
  const handleClose = () => {
    if (!isSubmitting) {
      setSectionTitle("");
      setSectionSubtitle("");
      setSectionType("Default");
      setMultipleElements(false);
      onClose();
    }
  };
  
  const stopEvent = (e: React.SyntheticEvent) => {
    e.preventDefault();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open && !isSubmitting) handleClose();
    }}>
      <DialogContent className="sm:max-w-[425px]" onClick={stopEvent}>
        <DialogHeader>
          <DialogTitle>Add Section</DialogTitle>
          <DialogDescription>
            Create a new section to organize related fields.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="section-title" className="text-right col-span-1 text-xs font-medium">
              Title
            </label>
            <Input
              id="section-title"
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              className="col-span-3"
              placeholder="Section title (optional)"
              disabled={isSubmitting}
              onClick={stopEvent}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="section-subtitle" className="text-right col-span-1 text-xs font-medium">
              Subtitle
            </label>
            <Input
              id="section-subtitle"
              value={sectionSubtitle}
              onChange={(e) => setSectionSubtitle(e.target.value)}
              className="col-span-3"
              placeholder="Section subtitle (optional)"
              disabled={isSubmitting}
              onClick={stopEvent}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="section-type" className="text-right col-span-1 text-xs font-medium">
              Type
            </label>
            <div className="col-span-3" onClick={stopEvent}>
              <Select
                value={sectionType}
                onValueChange={(value) => setSectionType(value as SectionType)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="section-type" onClick={stopEvent}>
                  <SelectValue placeholder="Select section type" />
                </SelectTrigger>
                <SelectContent position="popper" onClick={stopEvent}>
                  <SelectItem value="Default" onClick={stopEvent}>Default</SelectItem>
                  <SelectItem value="Applicants" onClick={stopEvent}>Applicants</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1" />
            <div className="flex items-center space-x-2 col-span-3">
              <Checkbox
                id="multiple-elements"
                checked={multipleElements}
                onCheckedChange={(checked) => {
                  console.log("Multiple elements checkbox changed:", checked);
                  setMultipleElements(checked === true);
                }}
                disabled={isSubmitting}
              />
              <Label
                htmlFor="multiple-elements"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                onClick={() => {
                  if (!isSubmitting) {
                    setMultipleElements(!multipleElements);
                  }
                }}
              >
                Allow multiple elements
              </Label>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              stopEvent(e);
              handleClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={handleAddSection}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Section"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddSectionDialog;
